import { Step } from "react-joyride";
import useStore from "../Store/Store";

// Define the onboardingConfig with the type of Step[]
const onboardingConfig: Step[] = [
  {
    target: "body",
    title: "🚀 Welcome to Code Canvas!",
    content:
      "Create documentations and architecture diagrams linked to source code, and let Code Canvas help in keeping them in sync with source code to never go obsolete.",
    placement: "center",
  },
  {
    target: ".search-bar-wrapper",
    placement: "bottom",
    title: "Your Codebase at your fingerprints",
    content:
      "Use the search feature to quickly find code and connect it to your diagram.",
  },
  {
    target: ".CodeTab",
    placement: "bottom",
    title: "Context Switch Begone",
    content: "Use Code tab to select and link blocks to diagram cells",
  },
  {
    target: ".SimulationTab",
    placement: "bottom",
    title: "Marry Business with Code",
    content:
      "Create Tutorials and runtime simulations visualized on diagram. Minimize the need for source code tracing to understand the business logic.",
  },
  {
    target: ".simulationsDropdown",
    placement: "bottom",
    title: "Enter the future now!",
    content: `Get started by selecting the "Tutorial: e-commerce site architecture" simulation from the dropdown`,
  },
];

// New simulation onboarding configuration that triggers after AI simulations are generated
const simulationOnboardingConfig: Step[] = [
  {
    target: ".simulationsCardsContainer",
    placement: "left",
    title: `🎉 Your Repository Analysis is Complete!`,
    content:
      "• The AI model reasoned up these use-cases/functionalities of the codebase (we call them simulations).\n\n\n\n• After completing this tutorial, click on any use-case to learn how it's implemented in the codebase through a guided runtime simulation.",
    styles: {
      options: {
        arrowColor: "#fff",
        backgroundColor: "#fff",
        overlayColor: "rgba(0, 0, 0, 0.4)",
        primaryColor: "#ffaea6",
      },
    },
    locale: {
      skip: "Skip Tutorial",
      close: "Close",
      last: "Finish",
      next: "Next",
      back: "Back",
    },
  },
  {
    target: ".custom-usecase-input",
    placement: "left",
    title: "✨ Create Custom Use Cases",
    content:
      "If you would like to explore another usecase of your codebase that's not mentioned in the simulations below, simply write it down here and AI will do its magic!",
    styles: {
      options: {
        arrowColor: "#ffaea6",
        backgroundColor: "#fff",
        overlayColor: "rgba(0, 0, 0, 0.4)",
        primaryColor: "#ffaea6",
      },
    },
  },
  {
    target: '.simulationsCardsContainer [data-testid^="simulation-card-"]',
    placement: "left",
    title: "🎯 Select a Use Case", // Dont change this title, it's used to identify the step
    content:
      "We've pre-generated your first simulation, Click the highlighted simulation to open it.",
    styles: {
      options: {
        arrowColor: "#ffaea6",
        backgroundColor: "#fff",
        overlayColor: "rgba(0, 0, 0, 0.4)",
        primaryColor: "#ffaea6",
      },
    },
    locale: {
      skip: "Skip Tutorial",
      close: "Close",
      last: "Finish",
      next: "Click the highlighted button",
      back: "Back",
    },
  },
  {
    target: ".SimStepsList",
    placement: "left",
    title: "🔄 Simulation Steps (SimSteps)",
    content:
      "These are the codebase runtime execution steps that implement this usecase, we call them simulation steps (or SimSteps).",
    styles: {
      options: {
        arrowColor: "#ffaea6",
        backgroundColor: "#fff",
        overlayColor: "rgba(0, 0, 0, 0.4)",
        primaryColor: "#ffaea6",
      },
    },
  },
  {
    target: ".simulationControlButton.stop",
    placement: "left",
    title: "▶️ Start the Simulation", // Dont change this title, it's used to identify the step
    content:
      "Hit the play button to start the selected usecase's runtime simulation!",
    styles: {
      options: {
        arrowColor: "#ffaea6",
        backgroundColor: "#fff",
        overlayColor: "rgba(0, 0, 0, 0.4)",
        primaryColor: "#ffaea6",
      },
    },
    locale: {
      skip: "Skip Tutorial",
      close: "Close",
      last: "Finish",
      next: "Click the highlighted button",
      back: "Back",
    },
  },
  {
    target: ".SimStepsList",
    placement: "right",
    title: "🎮 Simulation Dashboard Overview",
    content:
      "Now the simulation is running! Here's what you need to know:\n\n• This is the current selected step\n\n\n\n",
    styles: {
      options: {
        arrowColor: "#ffaea6",
        backgroundColor: "#fff",
        overlayColor: "rgba(0, 0, 0, 0.4)",
        primaryColor: "#ffaea6",
      },
    },
  },
  {
    target: "[data-testid='step-description-window']", // step description window
    placement: "left",
    title: "🔍 Each step has step description and input/output data examples",
    content:
      "Hover over the pink buttons to see the input/output data examples",
  },
  {
    target: "[data-testid='simulation-source-code-viewer']",
    placement: "left",
    title: "🔍 Each step is also linked to the source code that implements it",
    content: "Notice breadcrumb bar at the top of the source code window",
  },
  {
    target: "#onboarding-diagram-highlight", // custom highlight div
    placement: "right",
    title:
      "Finally, each step is also represented on the diagram canvas as a cell",
    content:
      "Your diagram will become more complete as you generate more simulations. Each simulation step's codeblocks will be inserted in their appropriate spot on the diagram as diagram cells.",
  },
  {
    target: '[data-testid="level-of-details-slider"]',
    placement: "top",
    title: "🎛️ Adjust Level of Details",
    content: "Adjust the diagram level of details using the slider",
    styles: {
      options: {
        arrowColor: "#ffaea6",
        backgroundColor: "#fff",
        overlayColor: "rgba(0, 0, 0, 0.4)",
        primaryColor: "#ffaea6",
      },
    },
  },
  {
    target: '[data-testid="centerGraphButton"]',
    placement: "top",
    title: "🎯 Center the Diagram",
    content:
      "You can center the diagram from here to get a better view of your selected cells or the entire diagram.",
    styles: {
      options: {
        arrowColor: "#ffaea6",
        backgroundColor: "#fff",
        overlayColor: "rgba(0, 0, 0, 0.4)",
        primaryColor: "#ffaea6",
      },
    },
  },
  {
    target: '[data-testid="file-menu-icon"]',
    placement: "left",
    title: "💰 Save Your Work!",
    content:
      "Parsing the source code and generating the use-cases require multiple costly AI model calls ($0.3 - $5 per generation), so don't forget to save the diagram to avoid unnecessary regenerations. Click the file menu and use 'Save diagram to <branch>' to save your work.",
    styles: {
      options: {
        arrowColor: "#ffaea6",
        backgroundColor: "#fff",
        overlayColor: "rgba(0, 0, 0, 0.4)",
        primaryColor: "#ffaea6",
      },
    },
  },
  {
    target: '[data-testid="optionsMenuButton"]',
    placement: "left",
    title: "🔧 Reset Cache When Needed",
    content:
      "CodeCanvas is still in Beta, if you experience unexpected behaviours, you can always reset the site cache from here. Look for the 'Reset Cache' option in this menu.",
    styles: {
      options: {
        arrowColor: "#ffaea6",
        backgroundColor: "#fff",
        overlayColor: "rgba(0, 0, 0, 0.4)",
        primaryColor: "#ffaea6",
      },
    },
  },
  {
    target: '[data-help-icon="true"]',
    placement: "left",
    title: "💡 Need Help?",
    content:
      "Use this help button anytime if you need assistance or replay the tutorial.",
    styles: {
      options: {
        arrowColor: "#ffaea6",
        backgroundColor: "#fff",
        overlayColor: "rgba(0, 0, 0, 0.4)",
        primaryColor: "#ffaea6",
      },
    },
  },
];

export default onboardingConfig;
export { simulationOnboardingConfig };
